/* Minimal Maskan — wishlist (favourites).
   Saved in this browser only (localStorage), as a list of product handles: no account or app needed,
   but favourites do not follow a customer to another browser or device.
   - Heart buttons: any element with [data-mm-wish="product-handle"] toggles that product.
   - Header count: [data-mm-wish-count].
   - Wishlist page: <mm-wishlist-page> renders saved products from /products/<handle>.js. */
(function () {
  const KEY = 'mm-wishlist-v1';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const HEART =
    '<svg class="mm-wish-heart__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.3s-7.6-4.7-7.6-10.2A4.4 4.4 0 0 1 12 7.3a4.4 4.4 0 0 1 7.6 2.8c0 5.5-7.6 10.2-7.6 10.2Z"/></svg>';

  /* If the browser blocks storage (some private modes), favourites last for this page view only. */
  let memory = [];
  const list = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(saved) ? [...new Set(saved.filter((h) => typeof h === 'string' && h))] : [];
    } catch (e) {
      return memory;
    }
  };
  const write = (handles) => {
    memory = [...new Set(handles)];
    try {
      localStorage.setItem(KEY, JSON.stringify(memory));
    } catch (e) {
      /* Storage blocked: keep the in-memory list. */
    }
    document.dispatchEvent(new CustomEvent('mm:wishlist-change'));
  };
  const has = (handle) => list().includes(handle);
  const toggle = (handle) => {
    const current = list();
    write(current.includes(handle) ? current.filter((h) => h !== handle) : [...current, handle]);
    return has(handle);
  };

  /* ---------- Hearts and header count ---------- */
  const sync = () => {
    const saved = list();
    document.querySelectorAll('[data-mm-wish]').forEach((button) => {
      const on = saved.includes(button.dataset.mmWish);
      const name = button.dataset.title || 'this item';
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-label', on ? `Remove ${name} from your wishlist` : `Save ${name} to your wishlist`);
      const text = button.querySelector('[data-mm-wish-text]');
      if (text) text.textContent = on ? text.dataset.on : text.dataset.off;
    });
    document.querySelectorAll('[data-mm-wish-count]').forEach((count) => {
      count.textContent = saved.length;
      count.hidden = saved.length === 0;
    });
    document.querySelectorAll('[data-mm-wish-label]').forEach((label) => {
      label.textContent = saved.length ? `Wishlist (${saved.length} saved)` : 'Wishlist';
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mm-wish]');
    if (!button) return;
    /* Hearts sit on product cards (which are links): never follow the link or add to cart. */
    event.preventDefault();
    event.stopPropagation();
    const on = toggle(button.dataset.mmWish);
    const live = document.getElementById('mm-wish-live');
    if (live) live.textContent = on ? 'Saved to your wishlist' : 'Removed from your wishlist';
  });
  document.addEventListener('mm:wishlist-change', sync);
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) document.dispatchEvent(new CustomEvent('mm:wishlist-change'));
  });
  /* Product cards loaded later (quick add, recommendations, filtering) get the right state too. */
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('[data-mm-wish]') || n.querySelector('[data-mm-wish]'))))) sync();
  }).observe(document.documentElement, { childList: true, subtree: true });
  sync();

  /* ---------- Wishlist page ---------- */
  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(cents / 100) + ' ' + currency;
  };
  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cache = {};
  const fetchProduct = (handle) =>
    (cache[handle] =
      cache[handle] ||
      fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => undefined));

  class MMWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-mm-wish-grid]');
      this.empty = this.querySelector('[data-mm-wish-empty]');
      this.render = this.render.bind(this);
      document.addEventListener('mm:wishlist-change', this.render);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('mm:wishlist-change', this.render);
    }
    async render() {
      const handles = list();
      this.empty.hidden = handles.length > 0;
      this.grid.hidden = handles.length === 0;
      if (!handles.length) {
        this.grid.innerHTML = '';
        return;
      }
      const products = await Promise.all(handles.map(fetchProduct));
      /* Drop favourites whose product no longer exists (404); keep them if the network failed. */
      const gone = handles.filter((h, i) => products[i] === null);
      if (gone.length) {
        write(list().filter((h) => !gone.includes(h)));
        return;
      }
      this.grid.innerHTML = products
        .map((p, i) => {
          if (!p) return '';
          const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
          const price = (p.price_varies ? 'From ' : '') + money(p.price_min);
          return `<li class="mm-wish-item">
            <a class="mm-wish-item__media" href="${escapeHtml(p.url)}" tabindex="-1" aria-hidden="true">${img ? `<img src="${escapeHtml(img)}" alt="" loading="lazy" width="600" height="600">` : ''}</a>
            <button type="button" class="mm-wish-heart mm-wish-heart--card" data-mm-wish="${escapeHtml(handles[i])}" data-title="${escapeHtml(p.title)}" aria-pressed="true" aria-label="Remove ${escapeHtml(p.title)} from your wishlist">${HEART}</button>
            <div class="mm-wish-item__info">
              <a class="mm-wish-item__title" href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a>
              <span class="mm-wish-item__price">${price}</span>
              ${p.available ? '' : '<span class="mm-wish-item__note">Sold out</span>'}
              <button type="button" class="mm-wish-item__remove" data-mm-wish="${escapeHtml(handles[i])}" data-title="${escapeHtml(p.title)}" aria-pressed="true">Remove</button>
            </div>
          </li>`;
        })
        .join('');
      sync();
    }
  }
  if (!customElements.get('mm-wishlist-page')) customElements.define('mm-wishlist-page', MMWishlistPage);
})();
